/**
 * Script Blocker für Cookie Consent
 * Blockiert Scripts bis zur Einwilligung und aktiviert sie danach
 */
import { pmLog, pmWarn, pmError } from './Logger.js';
import { hasServiceConsent } from './Consent.js';
import '../gtm-consent-policy.js';

/**
 * Bekannte First-Party-Cookie-Namen/-Präfixe (Suffix "*" = Prefix-Match) für
 * Dienste, deren Tracking-Skripte Cookies direkt auf der Domain dieser
 * Website setzen - im Unterschied zu reinen iframe-Embeds (YouTube, Vimeo,
 * Google Maps, ...), deren Cookies auf der Drittanbieter-Domain liegen
 * (z. B. youtube.com) und von hier aus per document.cookie technisch gar
 * nicht löschbar wären (Same-Origin-Policy).
 *
 * Bewusst konservativ/kuratiert statt 1:1 aus cookie-database.json
 * übernommen: die DB listet z. B. für Facebook auch facebook.com-eigene
 * Session-Cookies (xs, c_user, datr, ...), die niemals auf dieser Domain
 * gesetzt werden und daher hier nichts verloren haben.
 *
 * Wird ausschließlich für die aktive Cookie-Löschung bei Consent-Widerruf
 * genutzt (siehe deleteCookiesForService()/deleteCookiesForCategory()
 * unten) - NICHT für die Cookie-Anzeige im Settings-Modal, die weiterhin
 * die vollständige, dokumentierte Liste aus cookie-database.json zeigt.
 */
const KNOWN_FIRST_PARTY_COOKIES = {
    google_analytics: ['_ga', '_ga_*', '_gid', '_gat', '_gat_gtag_*', '_dc_gtm_*', '_gac_*', 'AMP_TOKEN', '__utma', '__utmb', '__utmc', '__utmt', '__utmz', '__utmv', '__utmx', '__utmxx', '_gali'],
    facebook_pixel: ['_fbp', '_fbc'],
    matomo: ['_pk_id*', '_pk_ref*', '_pk_ses*', '_pk_cvar', '_pk_hsr*', '_pk_testcookie*', 'mtm_consent', 'mtm_consent_removed', 'mtm_cookie_consent', 'matomo_ignore', 'matomo_sessid'],
    google_ads: ['_gcl_aw', '_gcl_gs', '_gcl_gb', '_gac_gb_*', 'FPGCLGB', 'FPGCLAW']
};

/**
 * Service → Kategorie, nur für die Zuordnung bei der Cookie-Löschung
 * benötigt (z. B. wenn die gesamte Kategorie "Statistik" widerrufen wird,
 * nicht nur ein einzelner Dienst). Dienste ohne first-party-Cookies (reine
 * Embeds) sind der Vollständigkeit halber mit aufgeführt, tragen aber
 * mangels Eintrag in KNOWN_FIRST_PARTY_COOKIES nichts zur Löschung bei.
 */
const SERVICE_CATEGORY_MAP = {
    google_analytics: 'analytics',
    matomo: 'analytics',
    wordpress_stats: 'analytics',
    facebook_pixel: 'marketing',
    google_ads: 'marketing',
    pinterest_pixel: 'marketing',
    youtube: 'external',
    vimeo: 'external',
    dailymotion: 'external',
    bunny_stream: 'external',
    spotify: 'external',
    google_maps: 'external',
    soundcloud: 'external',
    open_street_map: 'external',
    twitter: 'external',
    instagram: 'external',
    threads: 'external',
    pinterest: 'external'
};

export class ScriptBlocker {
    constructor() {
        // Standard-Kategorien
        this.consent = {
            necessary: true,    // Immer aktiv
            analytics: false,
            marketing: false,
            external: false
        };

        // Service-spezifische Consents
        this.serviceConsent = {};

        // Bereits aktivierte Kategorien tracken
        this.activatedCategories = new Set(['necessary']);

        // Bereits aktivierte Services tracken
        this.activatedServices = new Set();

        pmLog('ScriptBlocker initialized');
    }

    /**
     * Consent laden und gespeicherte Kategorien aktivieren
     */
    loadConsent(cookieData) {
        if (cookieData && cookieData.categories) {
            this.consent = { ...this.consent, ...cookieData.categories };
            pmLog('Loaded consent:', this.consent);

            // Service-spezifische Consents laden
            if (cookieData.services) {
                this.serviceConsent = { ...cookieData.services };
                pmLog('Loaded service consent:', this.serviceConsent);
            }

            // Update GTM Consent Mode (if enabled)
            this.updateGTMConsent();

            // Publish state for third-party tools (independent of GTM)
            this._publishConsentState();

            // Aktiviere bereits akzeptierte Services
            Object.keys(this.serviceConsent).forEach(service => {
                if (this.serviceConsent[service] && !this.activatedServices.has(service)) {
                    this.activateService(service);
                }
            });

            // Aktiviere bereits akzeptierte Kategorien
            Object.keys(this.consent).forEach(category => {
                if (this.consent[category] && !this.activatedCategories.has(category)) {
                    this.activateCategory(category);
                }
            });
        }
    }

    /**
     * Einzelnes Script aktivieren
     */
    activateScript(oldScript) {
        if (window.pixelmateGTMConsent && window.pixelmateGTMConfig && ScriptBlocker.getElementService(oldScript) === 'google_tag_manager') {
            window.pixelmateGTMConsent.activate();
            return;
        }
        try {
            // Neues Script-Element erstellen
            const newScript = document.createElement('script');
            newScript.type = oldScript.dataset.pixelmateScriptType || 'text/javascript';

            // Externe Quelle kopieren
            if (oldScript.dataset.src) {
                newScript.src = oldScript.dataset.src;
                pmLog('  → Loading external script:', newScript.src);
            }

            // Inline Code kopieren
            if (oldScript.textContent && oldScript.textContent.trim()) {
                try {
                    // Validate that content doesn't start with HTML tags
                    const content = oldScript.textContent.trim();
                    if (content.startsWith('<') && !content.startsWith('<!--')) {
                        pmError('  → Skipping inline script with invalid content (starts with HTML tag)');
                        return;
                    }

                    newScript.textContent = oldScript.textContent;
                    pmLog('  → Loading inline script');
                } catch (contentError) {
                    pmError('  → Error copying script content:', contentError);
                    return;
                }
            }

            // Attribute kopieren (außer type, data-src, data-consent, data-consent-service)
            Array.from(oldScript.attributes).forEach(attr => {
                if (!['type', 'data-src', 'data-consent', 'data-consent-service', 'data-pixelmate-script-type'].includes(attr.name)) {
                    try {
                        newScript.setAttribute(attr.name, attr.value);
                    } catch (attrError) {
                        pmWarn('  → Could not copy attribute:', attr.name, attrError);
                    }
                }
            });

            // Altes Script durch neues ersetzen
            if (!oldScript.isConnected || !oldScript.parentNode) {
                pmError('  → Script is detached from DOM, cannot activate');
                return;
            }
            try {
                oldScript.parentNode.replaceChild(newScript, oldScript);
            } catch (replaceError) {
                // Fallback für Race Conditions (z.B. Elementor verschiebt DOM-Knoten
                // zwischen querySelector und setTimeout-Callback)
                try {
                    oldScript.insertAdjacentElement('afterend', newScript);
                    oldScript.remove();
                } catch (fallbackError) {
                    pmError('  → Script activation failed (both strategies):', fallbackError);
                }
            }
        } catch (error) {
            pmError('Error activating script:', error, oldScript);
            // Log the script content for debugging
            if (oldScript.textContent) {
                pmError('Script content preview:', oldScript.textContent.substring(0, 200));
            }
        }
    }

    /**
     * Konvertiert youtube.com zu youtube-nocookie.com wenn useNoCookie aktiviert ist.
     * Prüft window.pixelmateConfig.activeServices.youtube.useNoCookie
     */
    maybeConvertToNoCookieUrl(url, service) {
        if (service !== 'youtube') return url;
        const useNoCookie = window.pixelmateConfig?.activeServices?.youtube?.useNoCookie;
        if (!useNoCookie) return url;
        if (!url || url.includes('youtube-nocookie.com')) return url;
        return url
            .replace(/https?:\/\/(www\.)?youtube\.com\//g, 'https://www.youtube-nocookie.com/')
            .replace(/https?:\/\/youtu\.be\//g, 'https://www.youtube-nocookie.com/embed/');
    }

    /**
     * Der Dienst, dem ein blockiertes Element zugeordnet ist.
     *
     * PHP kennzeichnet Scripts, Links und Bilder mit `data-consent-service`,
     * blockierte iframes dagegen mit `data-service` (siehe
     * ServiceInjector::process_script_pattern_tags() bzw. den iframe-Pfad in
     * activateService()). Beide Schreibweisen meinen dasselbe.
     *
     * @param {Element} element
     * @returns {string} Preset-Key oder '' für Elemente ohne Dienst-Bezug.
     */
    static getElementService(element) {
        return element?.dataset?.consentService || element?.dataset?.service || '';
    }

    /**
     * Hat der Besucher diesen Dienst AUSDRÜCKLICH abgewählt?
     *
     * Nur ein explizites `false` zählt. Ein fehlender Eintrag bedeutet "keine
     * Einzelentscheidung getroffen" - dann gilt die Kategorie-Entscheidung.
     * Das ist der Unterschied, auf dem die Kategorie-Freigabe unten aufbaut.
     *
     * @param {string} service
     * @returns {boolean}
     */
    isServiceDenied(service) {
        return service !== '' && this.serviceConsent[service] === false;
    }

    /**
     * Alle Elemente eines Kategorie-Selektors, abzüglich derer, deren Dienst
     * einzeln abgewählt wurde.
     *
     * @param {string} selector
     * @returns {Element[]}
     */
    elementsForCategoryActivation(selector) {
        return Array.from(document.querySelectorAll(selector))
            .filter(element => !(window.pixelmateGTMConfig && ScriptBlocker.getElementService(element) === 'google_tag_manager'))
            .filter(element => !this.isServiceDenied(ScriptBlocker.getElementService(element)));
    }

    /**
     * Alle blockierten Elemente einer Kategorie aktivieren.
     *
     * Regel: Kategorie akzeptiert => Element frei, AUSSER der zugehörige
     * Dienst wurde einzeln abgewählt.
     *
     * Bis dahin schlossen die Selektoren für Scripts und iframes
     * service-spezifische Elemente pauschal aus (`:not([data-consent-service])`).
     * PHP setzt aber IMMER beide Attribute (`data-consent-service` UND
     * `data-consent`, siehe ServiceInjector::maybe_block_enqueued_script()),
     * wodurch jedes domainbasiert blockierte Script/iframe ausschliesslich über
     * activateService() freizugeben war - also nur, wenn sein Preset-Key in der
     * services-Map des Consent-Cookies stand. Mehrere Pfade füllen die aber
     * nicht: "Alle akzeptieren" mit aktiver CSP speichert eine leere Map, das
     * Banner-"Auswahl speichern" übergibt gar keine, und
     * ScriptBlocker.acceptAll() sammelt die Keys nur aus dem DOM der aktuellen
     * Seite - ein Dienst, der erst auf einer Unterseite vorkommt, blieb dort
     * trotz erteilter Einwilligung blockiert.
     *
     * Links und Bilder verhielten sich schon vorher so (dort fehlte das
     * `:not(...)`), Scripts und iframes ziehen damit nach. Dieselbe Logik nutzt
     * auch die CSP serverseitig, siehe
     * CSP::get_v2_script_pattern_csp_sources() ("Service ODER Kategorie").
     */
    activateCategory(category) {
        // Prüfen ob bereits aktiviert
        if (this.activatedCategories.has(category)) {
            pmLog(`Category "${category}" already activated`);
            return;
        }

        pmLog(`Activating category: ${category}`);

        const blockedScripts = this.elementsForCategoryActivation(
            `script[type="text/plain"][data-consent="${category}"]`
        );

        pmLog(`Found ${blockedScripts.length} blocked script(s) for category`);

        // Aktiviere jedes Script
        blockedScripts.forEach((script, index) => {
            // Kleine Verzögerung zwischen Scripts für Abhängigkeiten
            setTimeout(() => {
                if (hasServiceConsent({ categories: this.consent, services: this.serviceConsent },
                    ScriptBlocker.getElementService(script), script.dataset.consent)) {
                    this.activateScript(script);
                }
            }, index * 50);
        });

        this.activateIframes(category);

        // Blockierte <link>-Tags (z.B. Google-Fonts-Stylesheets) dieser Kategorie.
        this.activateLinks(category);

        // Blockierte Bild-Ressourcen (img/source/input) dieser Kategorie.
        this.activateImagesForCategory(category);

        // Kategorie als aktiviert markieren
        this.activatedCategories.add(category);
        pmLog(`Category "${category}" activated`);
    }

    /**
     * Service-spezifische Scripts und iframes aktivieren
     */
    activateService(service) {
        if (service === 'google_tag_manager' && window.pixelmateGTMConfig) {
            window.pixelmateGTMConsent?.activate();
            return;
        }
        // Prüfen ob bereits aktiviert
        if (this.activatedServices.has(service)) {
            pmLog(`Service "${service}" already activated`);
            return;
        }

        pmLog(`Activating service: ${service}`);

        // Finde alle blockierten Scripts dieses Services
        const scriptSelector = `script[type="text/plain"][data-consent-service="${service}"]`;
        const blockedScripts = document.querySelectorAll(scriptSelector);

        pmLog(`Found ${blockedScripts.length} blocked script(s) for service`);

        // Aktiviere jedes Script
        blockedScripts.forEach((script, index) => {
            setTimeout(() => {
                if (this.serviceConsent[service] === true) this.activateScript(script);
            }, index * 50);
        });

        // Aktiviere service-spezifische iframes (data-consent-service Attribut, JS-blockiert)
        const iframeSelector = `iframe[data-consent-service="${service}"][data-src]`;
        const blockedIframes = document.querySelectorAll(iframeSelector);

        pmLog(`Found ${blockedIframes.length} JS-blocked iframe(s) for service`);

        blockedIframes.forEach(iframe => {
            if (iframe.dataset.src) {
                iframe.src = this.maybeConvertToNoCookieUrl(iframe.dataset.src, service);
                iframe.removeAttribute('data-src');
                iframe.removeAttribute('sandbox');
                pmLog('  → Activated JS-blocked service iframe:', iframe.src);
            }
        });

        // Aktiviere PHP-seitig blockierte iframes (data-service + data-consent="external")
        // PHP blockiert Iframes mit data-consent="external" und data-service="<serviceName>",
        // NICHT mit data-consent-service. Daher brauchen wir einen zusätzlichen Selector.
        const phpIframeSelector = `iframe[data-service="${service}"][data-consent="external"][data-src]:not([data-consent-service])`;
        const phpBlockedIframes = document.querySelectorAll(phpIframeSelector);

        pmLog(`Found ${phpBlockedIframes.length} PHP-blocked iframe(s) for service`);

        phpBlockedIframes.forEach(iframe => {
            if (iframe.dataset.src) {
                iframe.src = this.maybeConvertToNoCookieUrl(iframe.dataset.src, service);
                iframe.removeAttribute('data-src');
                iframe.removeAttribute('sandbox');
                // Display wiederherstellen falls versteckt
                if (iframe.style.display === 'none') {
                    iframe.style.display = '';
                }
                pmLog('  → Activated PHP-blocked service iframe:', iframe.src);
            }
        });

        // Aktiviere blockierte <link>-Tags dieses Services (z.B. Google Fonts)
        this.activateLinkService(service);

        // Aktiviere blockierte Bild-Ressourcen (img/source/input) dieses Services
        this.activateImagesForService(service);

        // Service als aktiviert markieren
        this.activatedServices.add(service);
        pmLog(`Service "${service}" activated`);
    }

    /**
     * Stellt blockierte <link>-Tags einer Kategorie wieder her.
     * PHP (ServiceInjector) ersetzt href durch data-pixelmate-href, damit der
     * Browser die Ressource (z.B. Google-Fonts-Stylesheet) ohne Consent nicht lädt.
     *
     * Fonts sind render-relevant und sollen zurückkommen, sobald der Zweck
     * (Kategorie) akzeptiert wurde - das war schon vorher so und bleibt es.
     * Neu ist nur, dass eine AUSDRÜCKLICHE Abwahl des Dienstes im Modal jetzt
     * auch hier sticht: bislang wurde ein einzeln abgewählter Font-/CDN-Dienst
     * über die Kategorie trotzdem freigegeben. Ein fehlender Eintrag in der
     * services-Map ändert dagegen nichts (siehe isServiceDenied()).
     */
    activateLinks(category) {
        this.elementsForCategoryActivation(`link[data-pixelmate-href][data-consent="${category}"]`)
            .forEach(link => this.activateLink(link));
    }

    /**
     * Stellt blockierte <link>-Tags eines Services wieder her.
     */
    activateLinkService(service) {
        const selector = `link[data-pixelmate-href][data-consent-service="${service}"]`;
        document.querySelectorAll(selector).forEach(link => this.activateLink(link));
    }

    /**
     * Stellt ein einzelnes blockiertes <link>-Tag wieder her (href zurücksetzen).
     * Idempotent: nach der Wiederherstellung entfällt data-pixelmate-href, sodass
     * das Element von keinem Selector erneut erfasst wird.
     */
    activateLink(link) {
        const href = link.getAttribute('data-pixelmate-href');
        if (!href) return;
        link.setAttribute('href', href);
        link.removeAttribute('data-pixelmate-href');
        pmLog('  → Activated blocked link:', href);
    }

    /**
     * Stellt blockierte Bild-Ressourcen einer Kategorie wieder her.
     * PHP ersetzt src/srcset durch data-pixelmate-src/data-pixelmate-srcset,
     * damit der Browser die Ressource ohne Consent nicht lädt.
     */
    activateImagesForCategory(category) {
        const selector = [
            `[data-pixelmate-src][data-consent="${category}"]`,
            `[data-pixelmate-srcset][data-consent="${category}"]`
        ].join(',');
        this.elementsForCategoryActivation(selector).forEach(element => this.activateImage(element));
    }

    /**
     * Stellt blockierte Bild-Ressourcen eines Services wieder her.
     */
    activateImagesForService(service) {
        const selector = [
            `[data-pixelmate-src][data-consent-service="${service}"]`,
            `[data-pixelmate-srcset][data-consent-service="${service}"]`
        ].join(',');
        document.querySelectorAll(selector).forEach(element => this.activateImage(element));
    }

    /**
     * Stellt eine einzelne blockierte Bild-Ressource wieder her (src/srcset).
     * Idempotent: nach der Wiederherstellung entfallen die data-pixelmate-Attribute,
     * sodass das Element von keinem Selector erneut erfasst wird.
     */
    activateImage(element) {
        const src = element.getAttribute('data-pixelmate-src');
        const srcset = element.getAttribute('data-pixelmate-srcset');
        if (!src && !srcset) return;

        if (src) {
            element.setAttribute('src', src);
            element.removeAttribute('data-pixelmate-src');
        }
        if (srcset) {
            element.setAttribute('srcset', srcset);
            element.removeAttribute('data-pixelmate-srcset');
        }

        pmLog('  → Activated blocked image resource:', src || srcset);
    }

    /**
     * iframes einer Kategorie aktivieren - siehe activateCategory() für die
     * Regel "Kategorie frei, ausser der Dienst wurde einzeln abgewählt".
     */
    activateIframes(category) {
        const blockedIframes = this.elementsForCategoryActivation(
            `iframe[data-consent="${category}"][data-src]`
        );

        pmLog(`Found ${blockedIframes.length} blocked iframe(s) for category`);

        blockedIframes.forEach(iframe => {
            if (iframe.dataset.src) {
                const service = iframe.dataset.service || '';
                iframe.src = this.maybeConvertToNoCookieUrl(iframe.dataset.src, service);
                iframe.removeAttribute('data-src');
                // sandbox-Attribut entfernen, damit der Dienst voll funktionsfähig ist
                iframe.removeAttribute('sandbox');
                // Display wiederherstellen falls versteckt
                if (iframe.style.display === 'none') {
                    iframe.style.display = '';
                }
                pmLog('  → Activated category iframe:', iframe.src);
            }
        });
    }

    /**
     * Prüft, ob der Browser für das aktuelle Dokument jemals eine echte
     * Nutzerinteraktion (Klick, Tastendruck, Touch) registriert hat.
     *
     * Dient als Schutz gegen automatisiertes/programmatisches Erteilen von
     * Consent durch Skripte, die ohne Zutun des Besuchers laufen (z. B. ein
     * kompromittiertes Drittanbieter-Skript, das versucht, sich selbst über
     * updateConsent()/acceptAll() freizuschalten). Nutzt die standardisierte
     * User-Activation-API (https://html.spec.whatwg.org/multipage/interaction.html#tracking-user-activation),
     * dieselbe Grundlage, auf der Browser z. B. Autoplay- oder Popup-Schutz
     * aufbauen.
     *
     * In Browsern ohne diese API (kein Support) wird nicht blockiert, um
     * bestehendes Verhalten nicht zu beeinträchtigen.
     */
    _hasUserActivation() {
        if (typeof navigator === 'undefined' || !navigator.userActivation) {
            return true;
        }
        return navigator.userActivation.hasBeenActive === true;
    }

    /**
     * Consent aktualisieren und neue Kategorien/Services aktivieren
     */
    updateConsent(categories, services = null) {
        // Nur neu erteilte (nicht bereits vorhandene) Zustimmungen erfordern
        // eine echte Nutzerinteraktion. Rücknahmen (false) und No-Op-Updates
        // bleiben davon unberührt, damit z. B. der automatische GPC-Widerruf
        // (siehe PixelmateBanner.applyGpcConsent) weiterhin ohne Klick funktioniert.
        const grantsNewCategoryConsent = Object.keys(categories).some(
            category => categories[category] && category !== 'necessary' && !this.consent[category]
        );
        const grantsNewServiceConsent = !!services && Object.keys(services).some(
            service => services[service] && !this.serviceConsent[service]
        );

        if ((grantsNewCategoryConsent || grantsNewServiceConsent) && !this._hasUserActivation()) {
            pmWarn('Consent-Erteilung ohne erkennbare Nutzerinteraktion blockiert.', categories, services);
            return {
                categories: this.consent,
                services: this.serviceConsent
            };
        }

        pmLog('Updating consent:', categories, 'Services:', services);

        // Commit the complete decision before activation: a category must never
        // observe the previous service map when the same save denies a service.
        const previousCategories = this.consent;
        const previousServices = this.serviceConsent;
        this.consent = { ...previousCategories, ...categories, necessary: true };
        this.serviceConsent = { ...previousServices, ...(services || {}) };

        // Apply consent before any script or business event can execute.
        this.updateGTMConsent();
        // Activated inline scripts read window.pixelmateConsent synchronously
        // (e.g. the GA update deriving ad_storage from google_ads).
        this._exposeConsentState();

        Object.keys(categories).forEach(category => {
            if (category === 'necessary') return;
            if (this.consent[category] && !previousCategories[category]) {
                this.activateCategory(category);
            } else if (!this.consent[category]) {
                this.activatedCategories.delete(category);
                if (previousCategories[category]) this.deleteCookiesForCategory(category);
            }
        });
        Object.keys(services || {}).forEach(service => {
            if (this.serviceConsent[service] && !previousServices[service]) {
                this.activateService(service);
            } else if (!this.serviceConsent[service]) {
                this.activatedServices.delete(service);
                if (previousServices[service]) this.deleteCookiesForService(service);
            }
        });

        // Publish state for third-party tools (always runs, independent of GTM)
        this._publishConsentState();

        return {
            categories: this.consent,
            services: this.serviceConsent
        };
    }

    /**
     * Löscht first-party Cookies für Kategorien/Services, die durch eine
     * anstehende Consent-Änderung widerrufen werden würden - OHNE den
     * internen Consent-Status selbst zu verändern.
     *
     * Für die CSP-Reload-Pfade gedacht (siehe PixelmateBanner.saveSelection()
     * und SettingsModal.saveSelection()): dort wird updateConsent() bewusst
     * übersprungen, weil der bevorstehende Seiten-Reload die Skript-
     * Aktivierung/CSP-Whitelist ohnehin neu aufbaut. Die Cookie-Löschung muss
     * aber trotzdem VOR dem Reload passieren, solange der alte Consent-Stand
     * hier noch bekannt ist - danach überschreibt das neue Consent-Cookie
     * diesen Stand.
     */
    revokeCookiesForPendingChange(newCategories = {}, newServices = {}) {
        Object.keys(newCategories).forEach(category => {
            if (this.consent[category] && !newCategories[category]) {
                this.deleteCookiesForCategory(category);
            }
        });

        Object.keys(newServices).forEach(service => {
            if (this.serviceConsent[service] && !newServices[service]) {
                this.deleteCookiesForService(service);
            }
        });
    }

    /**
     * Löscht bekannte first-party-Cookies eines einzelnen Dienstes (siehe
     * KNOWN_FIRST_PARTY_COOKIES). No-op für Dienste ohne bekannte first-
     * party-Cookies (z. B. reine iframe-Embeds wie YouTube/Vimeo - deren
     * Cookies liegen auf einer fremden Domain und sind von hier aus nicht
     * löschbar).
     */
    deleteCookiesForService(service) {
        const patterns = KNOWN_FIRST_PARTY_COOKIES[service];
        if (!patterns || !patterns.length) return;
        this._deleteCookiesMatchingPatterns(patterns);
    }

    /**
     * Löscht bekannte first-party-Cookies aller Dienste einer Kategorie
     * (siehe SERVICE_CATEGORY_MAP), z. B. beim Widerruf der gesamten
     * "Statistik"-Kategorie statt eines einzelnen Dienstes.
     */
    deleteCookiesForCategory(category) {
        Object.keys(KNOWN_FIRST_PARTY_COOKIES).forEach(service => {
            if (SERVICE_CATEGORY_MAP[service] === category) {
                this.deleteCookiesForService(service);
            }
        });
    }

    /**
     * Löscht konkrete, bereits ermittelte Cookie-Namen (z. B. die vom
     * Settings-Modal über die Cookie-Datenbank einem widerrufenen Dienst
     * zugeordneten Cookies, siehe SettingsModal.collectCookiesToDelete()).
     */
    deleteCookiesByName(names = []) {
        const unique = Array.from(new Set(names.filter(Boolean)));
        if (!unique.length) return;

        unique.forEach(name => this._deleteCookieAllVariants(name));
        pmLog('Deleted cookies after consent revocation:', unique);
    }

    /**
     * Gleicht Cookie-Namen/-Präfix-Muster gegen die tatsächlich im Browser
     * gesetzten Cookie-Namen ab und löscht die Treffer.
     */
    _deleteCookiesMatchingPatterns(patterns) {
        const currentNames = document.cookie.split(';')
            .map(entry => entry.split('=')[0].trim())
            .filter(Boolean);

        const toDelete = new Set();
        patterns.forEach(pattern => {
            if (pattern.endsWith('*')) {
                const prefix = pattern.slice(0, -1);
                currentNames.forEach(name => {
                    if (name.startsWith(prefix)) toDelete.add(name);
                });
            } else if (currentNames.includes(pattern)) {
                toDelete.add(pattern);
            }
        });

        this.deleteCookiesByName(Array.from(toDelete));
    }

    /**
     * Löscht ein einzelnes Cookie über mehrere Domain-/Pfad-Kombinationen,
     * da document.cookie nur Cookies löschen kann, deren Domain/Pfad exakt
     * mit denen beim Setzen übereinstimmen.
     *
     * Nutzt keine echte Public-Suffix-Liste: bei mehr als zwei Domain-
     * Labels wird zusätzlich die naive "letzte zwei Labels"-Elterndomain
     * probiert, da Tracking-Skripte (z. B. gtag.js) Cookies meist dort statt
     * auf der vollen (Sub-)Domain setzen. Bei Domains wie "beispiel.co.uk"
     * ist diese Heuristik ungenau (Elterndomain würde "co.uk" ergeben) -
     * das betroffene Cookie bleibt dann ggf. stehen. Kein Sicherheitsproblem,
     * nur eine verpasste Löschung.
     */
    _deleteCookieAllVariants(name) {
        const hostname = window.location.hostname;
        const domainParts = hostname.split('.');
        const domains = new Set([null, hostname, `.${hostname}`]);

        if (domainParts.length > 2) {
            const parentDomain = domainParts.slice(-2).join('.');
            domains.add(parentDomain);
            domains.add(`.${parentDomain}`);
        }

        const paths = new Set(['/', window.location.pathname]);

        domains.forEach(domain => {
            paths.forEach(path => {
                const cookieParts = [`${name}=`, 'expires=Thu, 01 Jan 1970 00:00:00 GMT', `path=${path}`];
                if (domain) cookieParts.push(`domain=${domain}`);
                document.cookie = cookieParts.join('; ');
            });
        });
    }

    /**
     * Alle Kategorien und Services akzeptieren
     */
    acceptAll() {
        // Alle verfügbaren Services sammeln
        const allServices = Object.fromEntries(Object.keys(this.serviceConsent).map(service => [service, true]));
        const serviceElements = document.querySelectorAll('[data-consent-service]');
        serviceElements.forEach(el => {
            const service = el.dataset.consentService;
            if (service) {
                allServices[service] = true;
            }
        });

        return this.updateConsent({
            necessary: true,
            analytics: true,
            marketing: true,
            external: true
        }, allServices);
    }

    /**
     * Alle nicht-notwendigen Kategorien und Services ablehnen
     */
    rejectAll() {
        // Alle verfügbaren Services sammeln und auf false setzen
        const allServices = Object.fromEntries(Object.keys(this.serviceConsent).map(service => [service, false]));
        const serviceElements = document.querySelectorAll('[data-consent-service]');
        serviceElements.forEach(el => {
            const service = el.dataset.consentService;
            if (service) {
                allServices[service] = false;
            }
        });

        return this.updateConsent({
            necessary: true,
            analytics: false,
            marketing: false,
            external: false
        }, allServices);
    }

    /**
     * Send Consent Mode v2 updates to Google Tag Manager (if configured) and
     * push the pixelmate_consent_update event into the dataLayer.
     *
     * Das dataLayer-Event ist bewusst unabhängig von Consent Mode und von der
     * GTM-Karte: Die Option "Wird über Google Tag Manager eingebunden"
     * (GA/Ads/Facebook/Matomo) baut darauf als GTM-Trigger auf, auch wenn der
     * Container von einem anderen Plugin/Theme geladen wird oder Consent Mode
     * abgeschaltet ist.
     */
    updateGTMConsent() {
        if (window.pixelmateGTMConsent && window.pixelmateGTMConfig) {
            window.pixelmateGTMConsent.update({ categories: this.consent, services: this.serviceConsent });
            return;
        }
        const gtmConfig = window.pixelmateGTMConfig || null;
        const debug = !!(gtmConfig && gtmConfig.debug);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'pixelmate_consent_update',
            'pixelmate_consent': {
                categories: { ...this.consent },
                services: this._effectiveServiceConsent(),
                timestamp: Date.now()
            }
        });

        if (debug) {
            console.log('[Pixelmate Debug] Custom consent event pushed to dataLayer');
        }
    }

    // Google signals are resolved exclusively by gtm-consent-policy.js.

    /**
     * Consent-Update für bereits laufende Google-Tags auf der Seite der
     * Entscheidung, direkt vor einem CSP-Reload (Google verarbeitet das
     * Update dort, wo entschieden wurde).
     *
     * Bewusst nur die Speicher-Signale: keine pixelmate_<dienst>-Gates, kein
     * pixelmate_consent_update-Event, keine Script-Aktivierung. Das alles
     * holt der Reload mit der neuen CSP nach - vorher ausgelöst, feuerten
     * Basis-Tags (PageView, Leads) auf beiden Seiten.
     *
     * @param {Object} categories
     * @param {Object} services
     * @returns {boolean} ob eine Google-Bibliothek bereits läuft
     */
    sendConsentSignalsBeforeReload(categories, services = {}) {
        if (typeof window.gtag !== 'function') return false;
        const decision = {
            categories: { ...this.consent, ...categories, necessary: true },
            services: { ...this.serviceConsent, ...services }
        };
        const policy = window.pixelmateGTMConsent;
        const config = window.pixelmateGTMConfig;
        if (policy && config && !config.consentMode) return !!window.google_tag_data;
        const signals = policy && config
            ? policy.storageSignals(decision)
            : ScriptBlocker.googleStorageSignals(decision);
        window.gtag('consent', 'update', signals);
        return !!window.google_tag_data;
    }

    /**
     * Speicher-Signale für direkt eingebundenes GA/Google Ads, gleiche Regel
     * wie gtm-consent-policy.js resolve(): Einzelentscheidung vor Kategorie.
     *
     * @param {{categories: Object, services: Object}} decision
     * @returns {Object}
     */
    static googleStorageSignals({ categories, services }) {
        const allowed = (service, category) => (typeof services[service] === 'boolean' ? services[service] : !!categories[category]);
        const signal = value => (value ? 'granted' : 'denied');
        const ads = signal(allowed('google_ads', 'marketing'));
        return {
            analytics_storage: signal(allowed('google_analytics', 'analytics')),
            ad_storage: ads,
            ad_user_data: ads,
            ad_personalization: ads
        };
    }

    /**
     * Service-Map für das dataLayer-Event nach der Regel "Kategorie gewinnt,
     * Einzelabwahl sticht". this.serviceConsent allein reicht nicht: mehrere
     * Consent-Pfade (Banner-"Auswahl speichern", CSP-Reload) füllen sie nicht,
     * pixelmate_consent.services.<key> wäre dann undefined und ein darauf
     * aufbauender GTM-Trigger feuerte nie.
     */
    _effectiveServiceConsent() {
        if (!this._knownServiceCategories) {
            this._knownServiceCategories = new Map();
        }
        // Aktivierte Scripts verlieren data-consent-service, darum gemerkt.
        document.querySelectorAll('[data-consent-service][data-consent]').forEach(el => {
            const key = el.dataset.consentService;
            if (key && !this._knownServiceCategories.has(key)) {
                this._knownServiceCategories.set(key, el.dataset.consent);
            }
        });

        const services = { ...this.serviceConsent };
        this._knownServiceCategories.forEach((category, key) => {
            if (services[key] === true || services[key] === false) return;
            services[key] = !!this.consent[category];
        });
        return services;
    }

    /**
     * Exposes the current consent state globally and dispatches a DOM event.
     * Allows third-party tools and plugins to read or react to consent changes
     * without requiring GTM or IAB TCF.
     *
     * window.pixelmateConsent wird bewusst als nicht-überschreibbares,
     * eingefrorenes Objekt veröffentlicht: Andere Skripte auf der Seite sollen
     * den Consent-Zustand lesen, aber nicht durch einfaches Überschreiben von
     * window.pixelmateConsent vortäuschen können. Die eigentliche Erteilung
     * bleibt ausschließlich über updateConsent()/acceptAll()/rejectAll()
     * möglich (dort durch _hasUserActivation() geschützt).
     */
    _publishConsentState() {
        const state = this._exposeConsentState();

        document.dispatchEvent(new CustomEvent('pixelmate:consent', {
            bubbles: true,
            detail: state
        }));
    }

    /**
     * Sets window.pixelmateConsent without dispatching pixelmate:consent.
     * updateConsent() calls this before activating scripts, so their inline
     * code sees the decision that released them, not the previous one.
     *
     * @returns {Object} the frozen state
     */
    _exposeConsentState() {
        const state = Object.freeze({
            categories: Object.freeze({ ...this.consent }),
            services: Object.freeze({ ...this.serviceConsent }),
            timestamp: Date.now(),
            hasConsent: (category) => !!this.consent[category]
        });

        try {
            Object.defineProperty(window, 'pixelmateConsent', {
                value: state,
                writable: false,
                configurable: true,
                enumerable: true
            });
        } catch (e) {
            window.pixelmateConsent = state;
        }
        return state;
    }
}
